import type { AxiosRequestConfig } from 'axios';
import { api } from '@/config/api';

// Enums mirror backend/src/schemas/property*.schema.ts and dto/real-estate.dto.ts.
export const PROPERTY_TYPES = ['building', 'tower', 'compound', 'villa', 'townhouse', 'office_building', 'retail', 'warehouse', 'land', 'other'] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];
export const UNIT_TYPES = ['apartment', 'studio', 'penthouse', 'villa', 'townhouse', 'office', 'shop', 'warehouse', 'plot', 'room', 'other'] as const;
export type UnitType = (typeof UNIT_TYPES)[number];
export const UNIT_PURPOSES = ['rent', 'sale', 'both'] as const;
export type UnitPurpose = (typeof UNIT_PURPOSES)[number];
export const UNIT_STATUSES = ['available', 'reserved', 'rented', 'sold', 'off_market'] as const;
export type UnitStatus = (typeof UNIT_STATUSES)[number];
export const RENT_PERIODS = ['yearly', 'monthly'] as const;
export type RentPeriod = (typeof RENT_PERIODS)[number];
export const FURNISHINGS = ['furnished', 'semi_furnished', 'unfurnished'] as const;
export type Furnishing = (typeof FURNISHINGS)[number];
export const AREA_UNITS = ['sqft', 'sqm', 'marla', 'kanal', 'sqyd'] as const;
export type AreaUnit = (typeof AREA_UNITS)[number];
/** Post-dated cheques / instalments per lease term. */
export const LEASE_PAYMENTS = [1, 2, 3, 4, 6, 12] as const;
export type LeasePayments = (typeof LEASE_PAYMENTS)[number];
export const VIEWING_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show'] as const;
export type ViewingStatus = (typeof VIEWING_STATUSES)[number];
export const VIEWING_INTEREST = ['hot', 'warm', 'cold'] as const;
export type ViewingInterest = (typeof VIEWING_INTEREST)[number];
export const LEASE_STATUSES = ['active', 'ended', 'terminated'] as const;
export type LeaseStatus = (typeof LEASE_STATUSES)[number];
export type LeasePhase = 'upcoming' | 'current' | 'expiring' | 'past_end' | 'ended' | 'terminated';

export interface ListResponse<T> { items: T[]; total: number; page: number; limit: number }

// ── Properties & units ─────────────────────────────────────────────────────

export interface Property {
  propertyUid: string;
  companyUid: string;
  name: string;
  type: PropertyType;
  community: string;
  city: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  ownerContactUid?: string;
  description?: string;
  amenities: string[];
  imageUrls: string[];
  yearBuilt?: number | null;
  createdByUserUid?: string;
  createdAt?: string;
  updatedAt?: string;
  ownerName?: string;
}

/** `total` plus one count per unit status that has units. */
export type UnitCounts = { total: number } & Partial<Record<UnitStatus, number>>;
export interface PropertyListItem extends Property { unitCounts: UnitCounts }

export interface UnitPropertyRef { propertyUid: string; name: string; type: PropertyType; community: string; city: string }

export interface PropertyUnit {
  unitUid: string;
  companyUid: string;
  propertyUid: string;
  label: string;
  reference?: string;
  unitType: UnitType;
  purpose: UnitPurpose;
  status: UnitStatus;
  /** 0 = studio. */
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  areaUnit: string;
  floor?: number | null;
  furnishing?: Furnishing | null;
  parkingSpaces?: number | null;
  rentPrice?: number | null;
  rentPeriod: RentPeriod;
  salePrice?: number | null;
  currency: string;
  availableFrom?: string | null;
  /** Effective owner — the unit's own override, else the property's owner. */
  ownerContactUid?: string;
  ownerName?: string;
  amenities: string[];
  imageUrls: string[];
  description?: string;
  notes?: string;
  /** Set while an active lease holds the unit. */
  currentLeaseUid?: string;
  createdAt?: string;
  updatedAt?: string;
  property: UnitPropertyRef | null;
}

export interface UnitCurrentLease {
  leaseUid: string;
  tenantContactUid: string;
  tenantName?: string;
  startDate: string;
  endDate: string;
  rentAmount: number;
  rentPeriod: RentPeriod;
}
export interface UnitDetail extends PropertyUnit { currentLease: UnitCurrentLease | null }

export interface PropertyDetail extends Property { units: PropertyUnit[] }

export interface PropertiesSummary {
  properties: number;
  units: number;
  available: number;
  reserved: number;
  rented: number;
  sold: number;
  offMarket: number;
  /** Rented share of units that can be let (excludes sold / off market); null with none. */
  occupancyPct: number | null;
  activeLeases: number;
  /** Company currency only — leases in other currencies are left out. */
  annualRentRoll: number;
  expiringIn60Days: number;
  currency: string;
}

/**
 * Unit fields for create / update. `null` clears a number, date or choice; an
 * empty string clears text. Money is in major units (AED 85000, not fils).
 */
export interface UnitFieldsInput {
  reference?: string;
  unitType?: UnitType;
  purpose?: UnitPurpose;
  status?: UnitStatus;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  areaUnit?: AreaUnit;
  floor?: number | null;
  furnishing?: Furnishing | null;
  parkingSpaces?: number | null;
  rentPrice?: number | null;
  rentPeriod?: RentPeriod;
  salePrice?: number | null;
  currency?: string;
  availableFrom?: string | null;
  ownerContactUid?: string;
  amenities?: string[];
  imageUrls?: string[];
  description?: string;
  notes?: string;
}
export interface CreateUnitInput extends UnitFieldsInput { label: string }
export interface UpdateUnitInput extends UnitFieldsInput { label?: string }

export interface PropertyFieldsInput {
  type?: PropertyType;
  community?: string;
  city?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  ownerContactUid?: string;
  description?: string;
  amenities?: string[];
  imageUrls?: string[];
  yearBuilt?: number | null;
}
export interface CreatePropertyInput extends PropertyFieldsInput {
  name: string;
  /** Units created with the property — a villa is one property with one unit. */
  units?: CreateUnitInput[];
}
export interface UpdatePropertyInput extends PropertyFieldsInput { name?: string }

export interface ListUnitsParams {
  q?: string;
  propertyUid?: string;
  status?: UnitStatus;
  purpose?: 'rent' | 'sale';
  unitType?: UnitType;
  /** "0" = studio, "5+" = five or more. */
  bedrooms?: string;
  /** Matches the property's community or city. */
  community?: string;
  /** Rent price, or sale price when purpose is 'sale'. Accepts "120k", "1.2m". */
  maxPrice?: string;
  page?: number;
  limit?: number;
}

export interface MatchCriteria {
  purpose?: 'rent' | 'sale';
  budgetMin?: number;
  budgetMax?: number;
  bedrooms?: number;
  /** "5+" — the bedroom count is a floor, not an exact ask. */
  bedroomsAtLeast?: boolean;
  unitType?: string;
  location?: string;
  furnishing?: string;
}
export interface MatchResult { score: number; reasons: string[]; warnings: string[] }

export interface MatchUnitsParams {
  leadUid?: string;
  purpose?: 'rent' | 'sale';
  budgetMin?: string;
  budgetMax?: string;
  bedrooms?: string;
  unitType?: string;
  location?: string;
}
export interface MatchUnitsResponse {
  lead: { leadUid: string; profileName?: string; phone?: string } | null;
  criteria: MatchCriteria;
  hasCriteria: boolean;
  items: Array<PropertyUnit & { match: MatchResult }>;
}

export interface MatchingLead {
  leadUid: string;
  name?: string;
  phone?: string;
  status: string;
  assignedToUserUid?: string;
  criteria: MatchCriteria;
  match: MatchResult;
}
export interface MatchingLeadsResponse { unitUid: string; items: MatchingLead[] }

export interface OwnerRow {
  contactUid: string;
  name?: string;
  phone?: string;
  email?: string;
  properties: number;
  units: number;
  rented: number;
  available: number;
}
export interface TenantRow {
  contactUid: string;
  name?: string;
  phone?: string;
  email?: string;
  leases: number;
  current: {
    leaseUid: string;
    endDate: string;
    rentAmount: number;
    rentPeriod: RentPeriod;
    currency: string;
    unitLabel?: string;
    propertyName?: string;
  } | null;
}

// ── Viewings ───────────────────────────────────────────────────────────────

export interface Viewing {
  viewingUid: string;
  companyUid: string;
  unitUid: string;
  propertyUid: string;
  leadUid?: string;
  contactUid?: string;
  customerName?: string;
  customerPhone?: string;
  agentUserUid?: string;
  agentName?: string;
  scheduledAt: string;
  durationMinutes: number;
  status: ViewingStatus;
  interest?: ViewingInterest | null;
  feedback?: string;
  notes?: string;
  completedAt?: string;
  cancelledAt?: string;
  createdByUserUid?: string;
  createdAt?: string;
  updatedAt?: string;
  unit: {
    unitUid: string;
    label: string;
    status: UnitStatus;
    unitType: UnitType;
    bedrooms?: number | null;
    purpose: UnitPurpose;
    rentPrice?: number | null;
    rentPeriod: RentPeriod;
    salePrice?: number | null;
    currency: string;
  } | null;
  property: { propertyUid: string; name: string; community: string; city: string } | null;
}

export interface ViewingsSummary {
  today: number;
  upcoming: number;
  /** Still "scheduled" more than two hours after their time. */
  awaitingFeedback: number;
  completedThisWeek: number;
  hotThisWeek: number;
}

export interface ListViewingsParams {
  status?: ViewingStatus;
  /** Today is the company's calendar day; upcoming defaults to scheduled only. */
  when?: 'today' | 'upcoming' | 'past';
  agentUserUid?: string;
  unitUid?: string;
  leadUid?: string;
  page?: number;
  limit?: number;
}

export interface CreateViewingInput {
  unitUid: string;
  leadUid?: string;
  contactUid?: string;
  customerName?: string;
  customerPhone?: string;
  agentUserUid?: string;
  /** UTC ISO instant. */
  scheduledAt: string;
  durationMinutes?: number;
  notes?: string;
}
export interface UpdateViewingInput {
  agentUserUid?: string;
  scheduledAt?: string;
  durationMinutes?: number;
  notes?: string;
}
/** Omitted fields keep their value; null interest / '' feedback clears it. */
export interface CompleteViewingInput { interest?: ViewingInterest | null; feedback?: string }

// ── Leases ─────────────────────────────────────────────────────────────────

export interface LeasePaymentScheduleItem { dueDate: string; amount: number }
export interface LeasePaymentSchedule { termMonths: number; totalRent: number; items: LeasePaymentScheduleItem[] }

export interface Lease {
  leaseUid: string;
  companyUid: string;
  unitUid: string;
  propertyUid: string;
  tenantContactUid: string;
  startDate: string;
  endDate: string;
  rentAmount: number;
  rentPeriod: RentPeriod;
  payments: number;
  securityDeposit?: number | null;
  currency: string;
  registrationNumber?: string;
  status: LeaseStatus;
  endedAt?: string;
  endReason?: string;
  renewedToLeaseUid?: string;
  renewedFromLeaseUid?: string;
  notes?: string;
  createdByUserUid?: string;
  createdAt?: string;
  updatedAt?: string;
  unit: { unitUid: string; label: string; unitType: UnitType; bedrooms?: number | null } | null;
  property: { propertyUid: string; name: string; community: string; city: string } | null;
  tenant: { contactUid: string; name: string; phone?: string; email?: string } | null;
  /** Negative once the end date has passed. */
  daysToEnd: number;
  phase: LeasePhase;
  schedule: LeasePaymentSchedule;
}

export interface LeasesSummary {
  currency: string;
  active: number;
  annualRentRoll: number;
  expiring30: number;
  expiring60: number;
  expiring90: number;
  /** Still active although the end date has passed. */
  pastEndDate: number;
  vacantForRent: number;
}

export interface ListLeasesParams {
  status?: LeaseStatus;
  /** Active leases ending within N days (forces status active). */
  expiringWithin?: number;
  unitUid?: string;
  tenantContactUid?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export interface CreateLeaseInput {
  unitUid: string;
  tenantContactUid: string;
  /** YYYY-MM-DD. */
  startDate: string;
  endDate: string;
  rentAmount: number;
  rentPeriod?: RentPeriod;
  payments?: LeasePayments;
  securityDeposit?: number;
  currency?: string;
  registrationNumber?: string;
  notes?: string;
}
export interface UpdateLeaseInput {
  endDate?: string;
  rentAmount?: number;
  payments?: LeasePayments;
  securityDeposit?: number | null;
  registrationNumber?: string;
  notes?: string;
}
export interface EndLeaseInput { status: 'ended' | 'terminated'; endedAt?: string; reason?: string }
export interface RenewLeaseInput { endDate: string; rentAmount?: number; payments?: LeasePayments; registrationNumber?: string }

type Cfg = Pick<AxiosRequestConfig, 'expectedStatuses'>;

export const realEstateService = {
  // Properties
  summary: async (): Promise<PropertiesSummary> => (await api.get('/slighter_crm/properties/summary')).data,
  listProperties: async (q?: string): Promise<{ items: PropertyListItem[] }> =>
    (await api.get('/slighter_crm/properties', { params: q ? { q } : {} })).data,
  getProperty: async (propertyUid: string, cfg?: Cfg): Promise<PropertyDetail> =>
    (await api.get(`/slighter_crm/properties/${propertyUid}`, cfg)).data,
  createProperty: async (p: CreatePropertyInput): Promise<PropertyDetail> => (await api.post('/slighter_crm/properties', p)).data,
  updateProperty: async (propertyUid: string, p: UpdatePropertyInput): Promise<PropertyDetail> =>
    (await api.patch(`/slighter_crm/properties/${propertyUid}`, p)).data,
  removeProperty: async (propertyUid: string): Promise<void> => { await api.delete(`/slighter_crm/properties/${propertyUid}`); },

  // Units
  listUnits: async (params: ListUnitsParams = {}): Promise<ListResponse<PropertyUnit>> =>
    (await api.get('/slighter_crm/properties/units', { params })).data,
  getUnit: async (unitUid: string): Promise<UnitDetail> => (await api.get(`/slighter_crm/properties/units/${unitUid}`)).data,
  createUnit: async (propertyUid: string, p: CreateUnitInput): Promise<UnitDetail> =>
    (await api.post(`/slighter_crm/properties/${propertyUid}/units`, p)).data,
  updateUnit: async (unitUid: string, p: UpdateUnitInput): Promise<UnitDetail> =>
    (await api.patch(`/slighter_crm/properties/units/${unitUid}`, p)).data,
  removeUnit: async (unitUid: string): Promise<void> => { await api.delete(`/slighter_crm/properties/units/${unitUid}`); },
  matchingLeads: async (unitUid: string): Promise<MatchingLeadsResponse> =>
    (await api.get(`/slighter_crm/properties/units/${unitUid}/matching-leads`)).data,
  matchUnits: async (params: MatchUnitsParams): Promise<MatchUnitsResponse> =>
    (await api.get('/slighter_crm/properties/match', { params })).data,
  owners: async (): Promise<{ items: OwnerRow[] }> =>
    (await api.get('/slighter_crm/properties/people', { params: { role: 'owner' } })).data,
  tenants: async (): Promise<{ items: TenantRow[] }> =>
    (await api.get('/slighter_crm/properties/people', { params: { role: 'tenant' } })).data,

  // Viewings
  viewingsSummary: async (): Promise<ViewingsSummary> => (await api.get('/slighter_crm/viewings/summary')).data,
  listViewings: async (params: ListViewingsParams = {}): Promise<ListResponse<Viewing>> =>
    (await api.get('/slighter_crm/viewings', { params })).data,
  getViewing: async (viewingUid: string): Promise<Viewing> => (await api.get(`/slighter_crm/viewings/${viewingUid}`)).data,
  /** A 409 is the agent's double-booking refusal — pass expectedStatuses to show it in the form. */
  createViewing: async (p: CreateViewingInput, cfg?: Cfg): Promise<Viewing> =>
    (await api.post('/slighter_crm/viewings', p, cfg)).data,
  updateViewing: async (viewingUid: string, p: UpdateViewingInput, cfg?: Cfg): Promise<Viewing> =>
    (await api.patch(`/slighter_crm/viewings/${viewingUid}`, p, cfg)).data,
  completeViewing: async (viewingUid: string, p: CompleteViewingInput): Promise<Viewing> =>
    (await api.post(`/slighter_crm/viewings/${viewingUid}/complete`, p)).data,
  cancelViewing: async (viewingUid: string): Promise<Viewing> => (await api.post(`/slighter_crm/viewings/${viewingUid}/cancel`)).data,
  noShowViewing: async (viewingUid: string): Promise<Viewing> => (await api.post(`/slighter_crm/viewings/${viewingUid}/no-show`)).data,
  removeViewing: async (viewingUid: string): Promise<void> => { await api.delete(`/slighter_crm/viewings/${viewingUid}`); },

  // Leases
  leasesSummary: async (): Promise<LeasesSummary> => (await api.get('/slighter_crm/leases/summary')).data,
  listLeases: async (params: ListLeasesParams = {}): Promise<ListResponse<Lease>> =>
    (await api.get('/slighter_crm/leases', { params })).data,
  getLease: async (leaseUid: string, cfg?: Cfg): Promise<Lease> => (await api.get(`/slighter_crm/leases/${leaseUid}`, cfg)).data,
  createLease: async (p: CreateLeaseInput): Promise<Lease> => (await api.post('/slighter_crm/leases', p)).data,
  updateLease: async (leaseUid: string, p: UpdateLeaseInput): Promise<Lease> =>
    (await api.patch(`/slighter_crm/leases/${leaseUid}`, p)).data,
  endLease: async (leaseUid: string, p: EndLeaseInput): Promise<Lease> =>
    (await api.post(`/slighter_crm/leases/${leaseUid}/end`, p)).data,
  /** Closes this lease and returns the NEW one (next term, same unit and tenant). */
  renewLease: async (leaseUid: string, p: RenewLeaseInput): Promise<Lease> =>
    (await api.post(`/slighter_crm/leases/${leaseUid}/renew`, p)).data,
  removeLease: async (leaseUid: string): Promise<void> => { await api.delete(`/slighter_crm/leases/${leaseUid}`); },
};
